import React from 'react';

const STATUS_TABLE: Array<{key: string; label: string; color: string; meaning: string; whenUse: string}> = [
  { key: 'scheduled', label: 'Scheduled', color: 'bg-yellow-100 text-yellow-800',
    meaning: 'Termin wurde angelegt, aber Patient hat (noch) nicht aktiv bestätigt.',
    whenUse: 'Standard-Status. Wird automatisch gesetzt, wenn ein neuer Termin erstellt wird.' },
  { key: 'confirmed', label: 'Confirmed', color: 'bg-green-100 text-green-800',
    meaning: 'Patient hat den Termin bestätigt (z.B. telefonisch, per E-Mail).',
    whenUse: 'Manuell setzen, wenn ihr Bestätigung erhalten habt.' },
  { key: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-800',
    meaning: 'Behandlung wurde durchgeführt.',
    whenUse: 'Nach dem Termin setzen — für Statistik und Historie.' },
  { key: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-800',
    meaning: 'Termin wurde storniert (von Patient oder Praxis).',
    whenUse: 'Wenn der Termin nicht stattfindet. Block bleibt im Kalender sichtbar (durchgestrichen / grau in Google).' },
  { key: 'no_show', label: 'No Show', color: 'bg-gray-200 text-gray-700',
    meaning: 'Patient ist ohne Absage nicht erschienen.',
    whenUse: 'Nach dem Termin setzen, wenn Patient nicht da war — getrennt von "cancelled" für Statistik.' },
];

const HelpPage: React.FC = () => {
  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Help &amp; Workflow</h1>
        <p className="text-sm text-gray-600">Wie funktioniert das Admin-System?</p>
      </div>

      {/* Status Workflow */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">📋 Termin-Status</h2>
        <p className="text-sm text-gray-700 mb-4">
          Jeder Termin hat einen Status. Den änderst du im <strong>Calendar</strong> über das Termin-Detail-Modal
          (Klick auf den Termin) oder direkt beim Bearbeiten.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Bedeutung</th>
                <th className="px-3 py-2 text-left font-medium text-gray-500">Wann setzen?</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {STATUS_TABLE.map(s => (
                <tr key={s.key}>
                  <td className="px-3 py-3"><span className={`px-2 py-1 text-xs rounded-full ${s.color}`}>{s.label}</span></td>
                  <td className="px-3 py-3 text-gray-700">{s.meaning}</td>
                  <td className="px-3 py-3 text-gray-600">{s.whenUse}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* What changes happen */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🔄 Was passiert beim Status-Wechsel?</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li>Status wird in der Datenbank gespeichert (sofort).</li>
          <li>Eintrag wird im <strong>Audit-Log</strong> dokumentiert (alter → neuer Status, wer, wann).</li>
          <li>Das passende <strong>Google-Calendar-Event</strong> wird aktualisiert:
            <ul className="ml-6 mt-1 space-y-1 list-circle list-inside text-gray-600">
              <li><code>cancelled</code>: Event wird in Google als <em>transparent</em> markiert (durchgestrichen/grau, blockiert keine Zeit mehr).</li>
              <li>Andere Status: Event bleibt <em>opaque</em> (normal sichtbar, Zeit ist geblockt).</li>
            </ul>
          </li>
          <li>Termin <strong>löschen</strong> (statt cancel): Eintrag wird komplett aus DB und Google Calendar entfernt.</li>
        </ul>
      </section>

      {/* Email behavior */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">✉️ Werden E-Mails verschickt?</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <strong className="text-green-900">Neu: Automatische Status-E-Mails</strong>
          </div>
          <p>E-Mails werden automatisch in folgenden Fällen an den Patienten verschickt:</p>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Bestätigung (Confirmed):</strong> Wenn Sie den Status auf "Confirmed" setzen, erhält der Patient eine Terminbestätigung.</li>
            <li><strong>Stornierung (Cancelled):</strong> Wenn Sie den Status auf "Cancelled" setzen, erhält der Patient eine Absage.</li>
          </ul>
          <p className="mt-3">Die E-Mails werden <strong>zweisprachig</strong> versendet (in der Muttersprache des Patienten und zusätzlich auf Englisch) und enthalten alle wichtigen Termindetails im sauberen HTML-Layout.</p>
          <p className="mt-2 text-gray-600">Jeder E-Mail-Versand wird im Hintergrund geprüft. Wenn Sie im Admin eine Änderung vornehmen, sehen Sie direkt im Anschluss ein kleines grünes Popup oben rechts ("Toast"), das den erfolgreichen Versand bestätigt. Alle gesendeten E-Mails werden zudem im <strong>Audit-Log</strong> dokumentiert.</p>
        </div>
      </section>

      {/* How to change */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🛠 Wo kann ich Status ändern?</h2>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li><strong>Calendar:</strong> Klick auf einen Termin im Kalender → Detail-Modal öffnet sich → Status-Buttons (Confirm/Complete/Cancel/No Show).</li>
          <li><strong>Calendar (Edit):</strong> Vom Detail-Modal aus auf "Bearbeiten" klicken → im Formular Status-Feld ändern.</li>
          <li><strong>Appointments-Liste:</strong> Listet alle Termine, "Open in Calendar" Link öffnet den Tag im Kalender direkt mit Detail-Modal.</li>
        </ol>
      </section>

      {/* Google Calendar */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">📅 Google Calendar Sync</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li>Termine im Admin werden automatisch in den verbundenen Google-Kalender geschrieben.</li>
          <li>Der "Sync Google Calendar" Button im Calendar-View zieht aktuelle Events zurück.</li>
          <li><strong>Pro Behandler ein eigener Kalender (empfohlen):</strong> Trag die Google Calendar ID jedes Behandlers in <em>Providers → Bearbeiten → Google Calendar ID</em> ein (z. B. <code>dra.garcia@seadent.es</code>). So werden Urlaube/private Termine, die direkt in Google eingetragen werden, automatisch als belegt erkannt und blockieren passende Slots auf seadent.es.</li>
          <li><strong>Wichtig — Freigabe für den Service-Account:</strong> Damit das funktioniert, muss jeder Provider-Kalender an den Service-Account freigegeben werden (Lese-Rechte reichen). Ohne Freigabe wird der Kalender stillschweigend als "immer frei" behandelt.</li>
        </ul>
      </section>

      {/* Online-Buchung */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🌐 Online-Buchung von Patienten</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Patienten buchen auf <strong>seadent.es</strong> ihren Wunschtermin direkt im Kalender.
            Es werden nur Tage und Uhrzeiten angeboten, an denen ein passender Behandler tatsächlich frei ist.
          </p>
          <p className="font-semibold text-gray-800 mt-3">So sieht der Patient den Ablauf:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Behandlung wählen (z. B. Zahnreinigung, Beratung, Notfall …).</li>
            <li>Versicherung angeben.</li>
            <li>Im Kalender einen Tag auswählen — graue Tage sind ausgebucht oder geschlossen.</li>
            <li>Eine freie Uhrzeit anklicken → Slot wird für 5 Minuten reserviert (Countdown im Formular).</li>
            <li>Patientendaten ausfüllen und absenden.</li>
          </ol>
          <p className="mt-3">
            Die Reservierung ist eine <strong>Soft-Reservierung</strong>: Wenn der Patient nicht in 5 Minuten absendet, wird der Slot wieder freigegeben. So gehen Slots nicht "verloren", wenn Formulare offen liegen bleiben.
          </p>
          <p>
            Eingehende Online-Buchungen landen mit Status <code>pending</code> im Kalender. Du bekommst eine E-Mail an <code>info@seadent.es</code>, prüfst den Termin und setzt den Status auf <code>confirmed</code> (oder <code>cancelled</code>) — der Patient bekommt automatisch die passende E-Mail.
          </p>
        </div>
      </section>

      {/* Patient-Termin-Zuordnung */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">👥 Wie werden Termine einem Patienten zugeordnet?</h2>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1.5">
          <li>
            <strong>Online-Buchung (seadent.es):</strong> Existiert ein Patient mit der eingegebenen
            E-Mail, wird der Termin angehängt. Sonst entsteht ein neuer Patient mit Quelle
            {' '}<code className="px-1 bg-gray-100 rounded text-xs">website</code>.
          </li>
          <li>
            <strong>Manuell im Backend / Google-Calendar-Import:</strong> Der Termin wird ohne
            Patient-Verknüpfung angelegt. Du ordnest den passenden Patienten anschließend im
            Termindetail über „Bearbeiten" zu — oder legst einen neuen an.
          </li>
        </ul>
      </section>

      {/* Resource-Type-Matching */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🧑‍⚕️ Wer macht welche Behandlung? (Rollen)</h2>
        <div className="space-y-3 text-sm text-gray-700">
          <p>
            Die Online-Buchung weiß, welche Behandlung von <strong>welchem Typ Behandler</strong> erbracht wird, und zeigt nur dann freie Slots, wenn der passende Typ verfügbar ist.
          </p>

          <div className="overflow-x-auto mt-3">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Wo</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Feld</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Bedeutung</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-3 py-3"><strong>Providers</strong></td>
                  <td className="px-3 py-3"><code>Rolle</code></td>
                  <td className="px-3 py-3">Welcher Typ ist diese Person? <em>Zahnarzt</em> oder <em>Hygiene</em>.</td>
                </tr>
                <tr>
                  <td className="px-3 py-3"><strong>Procedures</strong></td>
                  <td className="px-3 py-3"><code>Benötigte Rollen</code></td>
                  <td className="px-3 py-3">Welche Rolle(n) müssen für diese Behandlung frei sein? Standard: Zahnreinigung → Hygiene; alles andere → Zahnarzt.</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="mt-3 font-semibold text-gray-800">Beispiel:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Patient möchte eine <em>Zahnreinigung</em> am Dienstag um 10:00.</li>
            <li>Die Behandlung verlangt Rolle <em>Hygiene</em>.</li>
            <li>Die Hygienikerin ist um 10:00 frei → Slot wird angeboten ✅</li>
            <li>Wäre der Zahnarzt um 10:00 frei, aber die Hygienikerin nicht → Slot wäre <strong>nicht</strong> verfügbar.</li>
          </ul>

          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded mt-3">
            <strong className="text-yellow-900">Was du tun musst:</strong>
            <ul className="mt-1 list-disc list-inside space-y-1 text-yellow-900">
              <li>Bei jedem Behandler in <em>Providers</em> die <strong>Rolle</strong> setzen (Zahnarzt oder Hygiene).</li>
              <li>Bei jeder Behandlung in <em>Procedures</em> die <strong>Benötigten Rollen</strong> prüfen — die Defaults passen für die meisten Fälle.</li>
              <li>Optional bei jedem Behandler die <strong>Google Calendar ID</strong> hinterlegen, damit Urlaubs-/Privattermine aus Google die Online-Slots blockieren.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Stammdaten */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">⚙️ Stammdaten verwalten</h2>
        <ul className="space-y-2 text-sm text-gray-700 list-disc list-inside">
          <li><strong>Providers:</strong> Ärzte/Hygieniker — Name, Email, Telefon, <strong>Rolle</strong> (Zahnarzt/Hygiene), <strong>Google Calendar ID</strong> (optional). Inaktive werden in den Termin-Dropdowns und der Online-Buchung ausgeblendet.</li>
          <li><strong>Procedures:</strong> Behandlungen — Name (mehrsprachig), Beschreibung, Dauer (Minuten), Farbe, <strong>Benötigte Rollen</strong>, sowie der Schalter "Bookable on public site" (steuert, ob die Behandlung auf seadent.es überhaupt angeboten wird).</li>
          <li><strong>Operatories:</strong> Behandlungsräume — Name, Beschreibung. Wird beim Erstellen eines Termins ausgewählt (Konflikt-Check pro Raum).</li>
          <li>Beim Löschen oder Deaktivieren wird der Eintrag aus den Dropdowns ausgeblendet, aber bestehende Termine bleiben erhalten.</li>
        </ul>
      </section>

      {/* Patientenerhebungsbogen */}
      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">📝 Patientenerhebungsbogen</h2>

        <p className="text-sm text-gray-700 mb-4">
          Jeder Patient kann seinen Erhebungsbogen digital ausfüllen — auf dem Handy, am
          Tablet in der Praxis oder am eigenen Rechner. Verfügbar auf
          {' '}<strong>Deutsch, Englisch und Spanisch</strong> (Russisch in Vorbereitung).
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">1. Wo finde ich den Bogen?</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li>Im Termin-Detail-Modal (Klick auf einen Termin) gibt es jetzt einen Tab
            <span className="mx-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-semibold">Erhebungsbogen</span>.
            Dort findest du Link, QR-Code, Status und — sobald ausgefüllt — alle Antworten.</li>
          <li>Ein <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mx-0.5"></span> grüner Punkt am Tab signalisiert,
            dass der Patient den Bogen bereits ausgefüllt hat.</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">2. Wie entsteht der Link zum Bogen?</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li>Beim <strong>Anlegen eines Termins</strong> wird automatisch ein eindeutiger Token
            generiert (UUID) und am Termin gespeichert. Daraus baut sich der Link in der Form
            {' '}<code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">seadent.es/intake/&lt;token&gt;</code>.</li>
          <li>Der Link ist <strong>nicht erratbar</strong> und gilt nur für genau diesen einen Termin.</li>
          <li>Termine, die <em>vor</em> Einführung dieses Features angelegt wurden, bekommen ihren
            Token automatisch beim ersten Öffnen des Tabs „Erhebungsbogen" im Admin.</li>
          <li>Der Link bleibt gültig, bis der Patient den Bogen abgeschickt hat — danach ist er
            nur noch zur Anzeige (read-only) abrufbar.</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">3. Wie kommt der Link zum Patienten?</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li><strong>Link kopieren:</strong> Mit einem Klick in die Zwischenablage holen und per
            E-Mail, WhatsApp oder SMS schicken.</li>
          <li><strong>QR-Code anzeigen:</strong> In der Praxis das Modal öffnen, der Patient
            scannt den QR-Code mit dem Handy und fängt direkt an.</li>
          <li>Die Sprache richtet sich nach der am Termin hinterlegten Patienten-Sprache (de/en/es/ru).</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">4. Was passiert, wenn der Patient abschickt?</h3>
        <div className="text-sm text-gray-700 space-y-2 mb-4">
          <ol className="space-y-1.5 list-decimal list-inside">
            <li>Antworten + Unterschrift werden in der Datenbank gespeichert
              (verknüpft mit dem Termin).</li>
            <li>Aus den Antworten wird automatisch ein <strong>PDF erzeugt</strong> — Layout im
              Stil des bisherigen Sea-Dent-Papierbogens, inkl. Unterschrift und Status-Verlauf.</li>
            <li>Das PDF wird auf dem Server archiviert
              (<code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">server/uploads/intake/&lt;id&gt;.pdf</code>).</li>
            <li>Eine <strong>E-Mail mit PDF im Anhang geht automatisch an <a className="text-blue-600 hover:underline" href="mailto:info@seadent.es">info@seadent.es</a></strong>
              {' '}— inkl. Patient-Stammdaten, Termin-Info und Status-Historie.</li>
            <li>Der Termin wird auf <code className="text-xs bg-gray-100 px-1.5 py-0.5 rounded">intake_status: submitted</code> gesetzt;
              im Admin erscheint der grüne Punkt am Tab.</li>
            <li>Alle Schritte (Submit, Mail-Sent oder Mail-Failed) landen im
              <strong>Audit-Log</strong> des Termins.</li>
          </ol>
        </div>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">5. Auswertung im Admin</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside mb-4">
          <li><strong>Standardansicht</strong> zeigt nur die auffälligen Antworten („Ja"/„Unsicher")
            — das ist der typische Workflow vor dem Termin. „Alle Antworten zeigen" entfaltet den
            kompletten Bogen.</li>
          <li><strong>PDF öffnen / herunterladen:</strong> Buttons im Tab — fürs Drucken,
            Weiterleiten oder die digitale Akte.</li>
          <li><strong>Stammdaten</strong> (Name, Adresse, Hausarzt …), <strong>Medikamente</strong>,
            <strong>Spezialtherapien</strong> und die <strong>digitale Unterschrift</strong> sind
            direkt sichtbar.</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">6. Mail-Status & Fehlerfälle</h3>
        <div className="text-sm text-gray-700 space-y-2 mb-2">
          <p>Im Tab „Erhebungsbogen" siehst du oben den Versand-Status:</p>
          <ul className="space-y-1 list-disc list-inside ml-2">
            <li><span className="px-2 py-0.5 bg-green-100 text-green-800 rounded text-xs font-semibold">E-Mail versendet</span>
              {' '}— alles ok, Praxis hat den Bogen.</li>
            <li><span className="px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-semibold">E-Mail fehlgeschlagen</span>
              {' '}— der konkrete SMTP-Fehler wird darunter angezeigt (z. B. fehlende Credentials,
              Server nicht erreichbar). Mit <strong>„Erneut senden"</strong> nochmal versuchen.</li>
            <li><span className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-semibold">E-Mail ausstehend</span>
              {' '}— Bogen noch nicht ausgefüllt oder Versand läuft noch.</li>
          </ul>
          <p>Auch im Fehlerfall ist der Bogen <strong>nicht verloren</strong> — Daten und PDF sind
            in der DB/auf der Platte. Du kannst das PDF jederzeit herunterladen und die Mail
            manuell erneut auslösen.</p>
        </div>

        <h3 className="font-semibold text-gray-900 mb-2 mt-4">7. Datenschutz</h3>
        <ul className="space-y-1.5 text-sm text-gray-700 list-disc list-inside">
          <li>Der Patient bestätigt im letzten Schritt die <strong>elektronische Datenverarbeitung,
            24h-Absageregel</strong> und Kostenübernahme — wie auf dem bisherigen Papier-Bogen.</li>
          <li>Die digitale Unterschrift entspricht dem Standard für zahnärztliche
            Erstaufnahmen; keine qualifizierte E-Signatur nötig.</li>
          <li>Daten werden ausschließlich auf eurem Hetzner-Server gespeichert
            (kein Drittanbieter-Cloud-Speicher für Patientendaten).</li>
        </ul>
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">🧾 Belege: Angebote &amp; Rechnungen</h2>
        <p className="text-sm text-gray-700 mb-4">
          Belege sind das Modul für Kostenvoranschläge (Angebote), Rechnungen und Stornorechnungen.
          Es gibt drei Dokumenttypen, jeder mit eigener fortlaufender Nummer (Default
          {' '}<code className="px-1 bg-gray-100 rounded">AN-0001</code>,
          {' '}<code className="px-1 bg-gray-100 rounded">RE-0001</code>,
          {' '}<code className="px-1 bg-gray-100 rounded">ST-0001</code>).
          Die Nummer wird erst beim <strong>Festschreiben</strong> vergeben — solange ein Beleg
          ein Entwurf ist, kann er beliebig editiert oder verworfen werden, ohne Lücke in der Sequenz.
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">A) Einmalige Einrichtung</h3>
        <p className="text-sm text-gray-700 mb-2">
          Bevor erstmals abgerechnet wird, einmal alles unter
          {' '}<strong>Einstellungen → Rechnungsstellung</strong> ausfüllen:
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li><strong>Versand-Account (billing@…):</strong> ganz oben das Postfach für Rechnungen mit Passwort hinterlegen. Solange das Passwort fehlt, lassen sich keine Mails versenden. Nach dem Speichern den „Test"-Knopf nutzen, um eine Testmail an die eigene Adresse zu schicken.</li>
          <li><strong>Klinik-Absenderdaten:</strong> Firmenname inkl. Rechtsform, NIF, Anschrift, IBAN/BIC. Steht oben auf jeder Rechnung.</li>
          <li><strong>Voreinstellungen:</strong> Standard-MwSt (Default 21 %), Zahlungsziel (14 Tage), Gültigkeit von Angeboten (30 Tage), Standardsprache.</li>
          <li><strong>Nummernkreise:</strong> Präfix und Startwert. Achtung: nach dem ersten festgeschriebenen Beleg nicht mehr verändern.</li>
          <li><strong>Standardtexte je Sprache:</strong> Zahlungsbedingungen, Bankverbindung, MwSt-Befreiungs-Hinweis. Werden auf jedem neuen Beleg vorbefüllt.</li>
          <li><strong>E-Mail-Anschreiben:</strong> Betreff + Anschreiben für jede Sprache (DE/ES/EN) und jeden Dokumenttyp. Diese Vorlagen werden bei jedem Versand vorausgefüllt — im Versand-Dialog kannst du sie pro Mail nochmal verfeinern. Variablen wie <code className="px-1 bg-gray-100 rounded">{`{{number}}`}</code>, <code className="px-1 bg-gray-100 rounded">{`{{patient_name}}`}</code>, <code className="px-1 bg-gray-100 rounded">{`{{total}}`}</code> werden automatisch eingesetzt. Wir empfehlen einen warmen, persönlichen Ton — schließlich ist die Mail oft der erste Eindruck nach dem Praxisbesuch.</li>
          <li><strong>Leistungskatalog</strong> (unter <strong>Belege → Katalog</strong>): typische wiederkehrende Leistungen einmal als Vorlage anlegen — mit Bezeichnung in allen drei Sprachen, Preis, MwSt-Satz, Kategorie.</li>
          <li><strong>Schnellauswahl</strong> (zurück in <strong>Einstellungen → Rechnungsstellung</strong>): bis zu 8 Katalogeinträge wählen, die im „Behandlung abschließen"-Dialog als 1-Klick-Chips erscheinen.</li>
        </ol>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">B) Idealer Tagesablauf: Termin → Rechnung in einem Schritt</h3>
        <p className="text-sm text-gray-700 mb-2">
          Das ist der <strong>Hauptpfad</strong>. Sobald die Behandlung am Patienten beendet ist:
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-2 mb-4">
          <li>Im Kalender den Termin öffnen → Knopf <strong>„Behandlung abschließen"</strong> klicken.</li>
          <li>Im Dialog ist die Behandlung des Termins bereits als erste Position eingetragen.</li>
          <li>Weitere Leistungen hinzufügen:
            <ul className="list-disc list-inside ml-5 mt-1 space-y-1 text-gray-700">
              <li><strong>Quick-Chip klicken</strong> (z. B. „Lokalanästhesie 25 €") — Position wird sofort hinzugefügt. Erneutes Klicken erhöht die Menge.</li>
              <li><strong>„Aus Katalog suchen"</strong>, falls die gewünschte Leistung nicht als Chip vorgesehen ist.</li>
              <li><strong>„Freie Position"</strong> für einmalige Sonderleistungen.</li>
            </ul>
          </li>
          <li>Optional eine <strong>interne Notiz</strong> (nicht auf der Rechnung sichtbar) erfassen — z. B. „Nachkontrolle in 4 Wochen vereinbart".</li>
          <li>Einen der vier Ausgänge wählen:
            <ul className="list-disc list-inside ml-5 mt-1 space-y-1 text-gray-700">
              <li><strong>Direkt ausstellen</strong> — Standardfall: Termin wird abgeschlossen, Rechnung erhält ihre Nummer, PDF wird erzeugt. Anschließend springt die Ansicht in den Editor — von dort aus per Mail versenden.</li>
              <li><strong>Als Entwurf</strong> — wenn noch Feinschliff am Text nötig ist. Termin wird abgeschlossen, Rechnung bleibt editierbar.</li>
              <li><strong>Ohne Rechnung</strong> — z. B. Garantieleistung. Termin wird abgeschlossen, keine Rechnung erzeugt.</li>
              <li><strong>Abbrechen</strong> — nichts wird geändert.</li>
            </ul>
          </li>
          <li>Im Editor anschließend <strong>„Per Mail senden"</strong>: Empfänger und Sprache sind aus dem Patient vorbefüllt, Betreff und Anschreiben werden aus der gewählten Sprach-Vorlage geladen. <strong>Beide Texte sind direkt im Dialog editierbar</strong>, falls du für diesen einen Versand etwas Persönliches ergänzen möchtest. Die PDF hängt sich automatisch an.</li>
        </ol>
        <p className="text-sm text-gray-700 mb-4">
          Alle Aktionen (Erstellung, Festschreibung, Versand, Bezahlung, Stornierung) erscheinen automatisch im
          {' '}<strong>Verlauf</strong>-Tab des Termins und in der Belege-Liste des Patienten.
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">C) Kostenvoranschlag vor der Behandlung</h3>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li>Im zukünftigen Termin auf <strong>„Angebot erstellen"</strong> klicken.</li>
          <li>Im Editor Positionen wie bei einer Rechnung erfassen, dann <strong>Festschreiben</strong> → erhält Nummer <code className="px-1 bg-gray-100 rounded">AN-…</code> + PDF.</li>
          <li>Per Mail versenden (oder ausdrucken).</li>
          <li>Wenn der Patient bestätigt: im Angebot <strong>„Angebot angenommen"</strong> klicken.</li>
          <li>Danach <strong>„In Rechnung umwandeln"</strong> — alle Positionen werden in einen neuen Rechnungsentwurf übernommen, dieser kann nach der Behandlung schnell finalisiert werden.</li>
        </ol>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">D) Rechnung als bezahlt markieren</h3>
        <p className="text-sm text-gray-700 mb-4">
          Sobald die Zahlung eingegangen ist, in der Rechnung den Button
          {' '}<strong>„Als bezahlt markieren"</strong> klicken. Das ist nur ein Statuswechsel, kein
          neuer Beleg — die ursprüngliche Rechnung bleibt unverändert.
        </p>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">E) Stornierung &amp; Korrektur</h3>
        <p className="text-sm text-gray-700 mb-2">
          Festgeschriebene Belege können nicht mehr editiert werden — das ist gesetzlich Pflicht
          (lückenlose Nummernreihen, keine nachträglichen Änderungen). Stattdessen:
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li>In der falschen Rechnung auf <strong>„Stornieren"</strong> klicken, Grund eintragen.</li>
          <li>Es entsteht automatisch eine <strong>Stornorechnung</strong> (<code className="px-1 bg-gray-100 rounded">ST-…</code>) mit negativen Beträgen, eigener Nummer und PDF. Die Originalrechnung bekommt den Status „storniert".</li>
          <li>Anschließend bei Bedarf eine korrigierte Rechnung neu ausstellen (über den Termin, die Patientenakte oder „+ Rechnung" in der Belegliste).</li>
        </ol>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">F) Übersichten</h3>
        <ul className="list-disc list-inside text-sm text-gray-700 space-y-1 mb-4">
          <li><strong>Belegliste</strong> (Hauptmenü → Belege): Tabs für Angebote, Rechnungen, Storno. Filter nach Status, Patient, Zeitraum. KPIs für offene und überfällige Rechnungen.</li>
          <li><strong>Patientenakte → Block „Belege":</strong> alle Dokumente eines Patienten chronologisch, mit Verknüpfung zum jeweiligen Termin.</li>
          <li><strong>Termin → Verlauf-Tab:</strong> komplette Historie mit allen Invoice-Events des Termins.</li>
        </ul>

        <h3 className="font-semibold text-gray-900 mb-2 mt-5">G) Häufige Fragen</h3>
        <dl className="text-sm text-gray-700 space-y-3 mb-2">
          <div>
            <dt className="font-semibold text-gray-800">Welche MwSt-Sätze nutze ich für was?</dt>
            <dd className="ml-4">Zahnärztliche Heilbehandlung in Spanien (und DE) ist <strong>MwSt-frei</strong> (0 % nach Art. 20.Uno.3 LIVA). Ästhetische Eingriffe ohne medizinische Indikation und Verkauf von Produkten sind mit 21 % zu versteuern. Den korrekten Satz pro Position kann man im Katalog hinterlegen, sodass er automatisch übernommen wird.</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">Was ist mit Anschriften, wenn der Patient umzieht?</dt>
            <dd className="ml-4">Die Anschrift wird beim Anlegen der Rechnung als Snapshot übernommen. Spätere Änderungen in der Patientenakte wirken sich nicht auf bestehende Belege aus — das ist gewollt, weil eine festgeschriebene Rechnung die damalige Anschrift dokumentiert.</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">Warum kann ich meine Rechnung nicht mehr ändern?</dt>
            <dd className="ml-4">Sobald sie festgeschrieben ist, ist sie steuerlich verbindlich. Korrekturen laufen ausschließlich über Stornierung + neue Rechnung — das ist Pflicht, kein Bug.</dd>
          </div>
          <div>
            <dt className="font-semibold text-gray-800">Wo sehe ich, ob eine E-Mail durchkam?</dt>
            <dd className="ml-4">Im Verlauf des Termins erscheint <em>„Beleg per E-Mail versendet"</em> bei Erfolg bzw. <em>„E-Mail-Versand fehlgeschlagen"</em> bei Fehler. Im Editor selbst gibt es nach dem Versand eine Bestätigungs-Toast-Meldung.</dd>
          </div>
        </dl>
      </section>

      {/* Powered by LEANOFY */}
      <footer className="mt-12 pt-6 border-t border-gray-200 text-center text-sm text-gray-500">
        Powered by{' '}
        <a
          href="https://www.leanofy.de"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-brand-primary hover:underline"
        >
          LEANOFY
        </a>
        {' · '}
        Support &amp; Anfragen:{' '}
        <a
          href="https://www.leanofy.de"
          target="_blank"
          rel="noopener noreferrer"
          className="text-brand-primary hover:underline"
        >
          www.leanofy.de
        </a>
      </footer>
    </div>
  );
};

export default HelpPage;
